/**
 * One target, wherever a target is shown.
 *
 * A target is the same object on every surface — it names exactly ONE skill, it
 * carries a day, and its `state` is derived by the fold rather than written —
 * so the row that renders it is one component, not one per board. The learn
 * board shows a learner their own targets; the mentor board shows a lead a
 * trainee's, with the modules that lead there and a Drop control. Those two
 * additions are props; everything else is the same row.
 *
 * `state` is rendered, never computed. A row that decided "achieved" from a
 * date that has passed would tell a trainee she is done because time went by,
 * and one that decided it from a record on the fold would disagree with the
 * server the moment the fold's rule changed. The one thing a date decides here
 * is whether an OPEN target is late, and that comparison is `isOverdue`,
 * against a civil day the caller passes in.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Clock, Target } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { TargetState } from '@/api/endpoints/skills';
import { isOverdue, type SkillTarget } from '@/hooks/useSkills';
import { formatCivilDate } from '@/utils/dates';

export interface TargetRowProps {
  target: SkillTarget['target'];
  skillTitle: string;
  /**
   * Today in the reader's calendar (`todayCivilDate`), the day an open target's
   * `due_date` is late against.
   */
  today: string;
  /**
   * Titles of the modules that lead to this target, already resolved by the
   * caller — an archived module is gone from the published catalogue while the
   * target still names it, and what to show instead is the caller's answer.
   * Omitted on a surface that does not show the path to the target.
   */
  modules?: string[];
  /** A control the surface offers on this target, such as the mentor's Drop. */
  action?: ReactNode;
}

export function TargetRow({ target, skillTitle, today, modules, action }: TargetRowProps) {
  const { t, i18n } = useTranslation();
  const overdue = isOverdue(target, today);
  // Formatted once. Both branches of the due line ask for the same day, and the
  // exam line is the only other date on the row.
  const dueDate = formatCivilDate(target.due_date, i18n.language);

  return (
    <li className="rounded-[14px] border border-neutral-200 bg-white p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-neutral-900">{skillTitle}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-neutral-500">
            <span
              className={cn(
                'inline-flex items-center gap-1 tabular-nums',
                overdue && 'font-semibold text-[var(--color-tone-bad)]',
              )}
            >
              <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {overdue
                ? t('targets.overdue', { date: dueDate })
                : t('targets.due', { date: dueDate })}
            </span>
            {target.exam_date && (
              <span className="inline-flex items-center gap-1 tabular-nums font-semibold text-citation-700">
                <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t('targets.examDate', {
                  date: formatCivilDate(target.exam_date, i18n.language),
                })}
              </span>
            )}
            {modules && modules.length > 0 && (
              <span className="inline-flex min-w-0 items-center gap-1">
                <Target className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
                <span className="truncate">{modules.join(' · ')}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {target.state !== 'open' && <TargetStatePill state={target.state} />}
          {action}
        </div>
      </div>
    </li>
  );
}

/** Where the FOLD says the target stands. Read, never written — and never
 *  rendered for `open`, which the due date already states. */
function TargetStatePill({ state }: { state: Exclude<TargetState, 'open'> }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[12px] font-semibold',
        state === 'achieved'
          ? 'border-tone-ok/30 bg-tone-ok/10 text-tone-ok'
          : 'border-neutral-200 bg-neutral-50 text-neutral-500',
      )}
    >
      {t(`targets.state.${state}`)}
    </span>
  );
}
